import React, { useCallback, useEffect, useState, useRef } from 'react';
import '../../styles/onboardingFlow/onboarding/onboardingAddClient.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import ConnectMultipleSitesModal, {
  ConnectedShop,
} from '../onboardingComponents/ConnectMultipleSitesModal';
import AIURLInput from '../../ui/inputs/AIURLInput';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import OnboardingLogoPanel from '../onboardingComponents/OnboardingLogoPanel';
import OnboardingFooter from '../onboardingComponents/OnboardingFooter';
import {
  setAiOnboardingNextStep,
  setAiOnboardingStepCompleted,
  setCompanyStepDraft,
  setUserCompanyDetails,
  updateProfile,
} from '../../store/reducers/userSlice';
import { asyncHandler } from '../../lib/api/async-handler';
import { RequestInfo } from '../../interfaces/RequestInfo';
import {
  CREATE_SHOP_API,
  DELETE_SHOP_API,
  SHOP_API,
} from '../../lib/constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { submitLogToBugsnag } from '../../lib/api/log';
import update_all_shop_stats_api_call from '../../lib/api/dashboard/update_all_shop_stats_api_call';
import ensureHttps from '../../lib/helper-functions/ensureHttps';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import extractDomainName from '../../lib/helper-functions/extractDomainName';
import { setMessage } from '../../store/reducers/appSlice';

const OnboardingAddClient: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const seller_id = useSelector(
    (state: RootState) => state.user.profile.seller_id
  );
  const seller_email = useSelector(
    (state: RootState) => state.user.profile.email
  );
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const isInitSuccess = useSelector(
    (state: RootState) => state.app.isInitSuccess
  );
  const companyStepDraft = useSelector((state: RootState) => {
    const aiOnboarding = state.user.ai_onboarding as {
      companyStepDraft?: {
        userRole: 'seller' | 'agency';
        connectedShops?: ConnectedShop[];
        url?: string;
      } | null;
    };
    return aiOnboarding.companyStepDraft ?? null;
  });
  const allShopsFromProfile = useSelector(
    (state: RootState) => state.user.profile.all_shops
  );

  const userRole = userProfile?.role || companyStepDraft?.userRole || 'seller';

  const buildConnectedShopsFromInit = useCallback((): ConnectedShop[] => {
    if (!allShopsFromProfile || !Array.isArray(allShopsFromProfile)) return [];
    return allShopsFromProfile
      .filter((s) => s?.slug)
      .map((s) => {
        const rawUrl = s.webshop_url?.trim() ?? '';
        const url = rawUrl
          ? rawUrl.startsWith('http')
            ? rawUrl
            : `https://${rawUrl}`
          : s.name
          ? `https://${s.name}`
          : '';
        return {
          url,
          name: s.name ?? extractDomainName(rawUrl || s.slug || ''),
          slug: s.slug,
          id: s.id,
        };
      });
  }, [allShopsFromProfile]);

  const getInitialUrl = () => {
    if (companyStepDraft?.url) return companyStepDraft.url;
    const webshopUrl = userProfile?.all_shops?.[0]?.webshop_url;
    return webshopUrl ? webshopUrl.replace(/^https?:\/\//, '') : '';
  };

  const getInitialConnectedShops = (): ConnectedShop[] => {
    const fromInit = buildConnectedShopsFromInit();
    if (allShopsFromProfile && Array.isArray(allShopsFromProfile)) {
      return fromInit;
    }
    if (
      companyStepDraft?.connectedShops &&
      Array.isArray(companyStepDraft.connectedShops)
    ) {
      return companyStepDraft.connectedShops;
    }
    return [];
  };

  const [loading, setLoading] = useState<'pending' | 'initial'>('initial');
  const [url, setURL] = useState(getInitialUrl());
  const [connectedShops, setConnectedShops] = useState<ConnectedShop[]>(
    getInitialConnectedShops
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const url_prefix = 'https://';

  useEffect(() => {
    if (userRole !== 'agency' || !isInitSuccess) return;
    const fromInit = buildConnectedShopsFromInit();
    setConnectedShops(fromInit);
  }, [userRole, isInitSuccess, allShopsFromProfile, buildConnectedShopsFromInit]);

  useEffect(() => {
    dispatch(
      setCompanyStepDraft({
        userRole: userRole as 'seller' | 'agency',
        ...(userRole === 'agency' ? { connectedShops } : { url }),
      })
    );
  }, [userRole, url, connectedShops, dispatch]);

  async function createCompanyForSite(siteUrl: string): Promise<any | null> {
    try {
      const tempName = extractDomainName(siteUrl);
      const requestInfo: RequestInfo = {
        url: CREATE_SHOP_API(seller_id),
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder({
          name: tempName,
          currency: 'EUR',
          role: userRole,
        }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: `${accessToken.type} ${accessToken.sellerToken}`,
        },
      };

      const res: any = await asyncHandler(
        requestInfo,
        'ONBOARDING_ADD_COMPANY',
        'Company Added Successfully.',
        false,
        false,
        true
      );

      const response = res.data;
      if (res.actionType === 'ONBOARDING_ADD_COMPANY' && response.success === true) {
        return {
          name: tempName,
          currency: 'EUR',
          shop_id: response?.data?.id?.toString(),
          shop_slug: response?.data?.slug,
          location: response?.data?.location || '',
          is_active: !!response?.data?.is_active,
          initial: response?.data?.initial || '',
          webshop_url: ensureHttps(siteUrl),
          transaction_per_month: '',
        };
      }
      return null;
    } catch (exception) {
      submitLogToBugsnag('error', `${exception}`);
      return null;
    }
  }

  async function saveWebshopDataForSite(
    companyResponse: any,
    siteUrl: string
  ): Promise<boolean> {
    const sendingURL = ensureHttps(siteUrl);
    const requestInfo: RequestInfo = {
      url: SHOP_API + `/${companyResponse?.shop_slug}/setting/shop-general`,
      method: 'PUT',
      body: applicationWWWXRLEncodedBodyBuilder({ webshop_url: sendingURL }),
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/x-www-form-urlencoded',
        Authorization: `${accessToken.type} ${accessToken.sellerToken}`,
      },
    };

    const res: any = await asyncHandler(
      requestInfo,
      'ONBOARDING_WEB_SHOP_URL',
      t('Update Successfully.'),
      false,
      false,
      true
    );

    return (
      res?.actionType === 'ONBOARDING_WEB_SHOP_URL' &&
      res?.data?.success === true
    );
  }

  async function createShopForAgency(
    urlInput: string
  ): Promise<ConnectedShop | null> {
    const siteUrl = urlInput.trim().replace(/^https?:\/\//, '');
    if (!siteUrl) return null;
    const companyCreated = await createCompanyForSite(siteUrl);
    if (!companyCreated?.shop_slug) return null;
    const ok = await saveWebshopDataForSite(companyCreated, siteUrl);
    if (!ok) return null;
    return {
      url: ensureHttps(siteUrl),
      name: companyCreated.name ?? extractDomainName(siteUrl),
      slug: companyCreated.shop_slug,
      id: companyCreated.shop_id,
    };
  }

  async function updateShopForAgency(
    slug: string,
    urlInput: string
  ): Promise<boolean> {
    const siteUrl = urlInput.trim();
    if (!siteUrl) return false;
    const fullUrl = siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`;
    const requestInfo: RequestInfo = {
      url: SHOP_API + `/${slug}/setting/shop-general`,
      method: 'PUT',
      body: applicationWWWXRLEncodedBodyBuilder({ webshop_url: fullUrl }),
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/x-www-form-urlencoded',
        Authorization: `${accessToken.type} ${accessToken.sellerToken}`,
      },
    };
    const res: any = await asyncHandler(
      requestInfo,
      'ONBOARDING_WEB_SHOP_URL',
      t('Update Successfully.'),
      false,
      false,
      true
    );
    return (
      res?.actionType === 'ONBOARDING_WEB_SHOP_URL' &&
      res?.data?.success === true
    );
  }

  async function deleteShopForAgency(slug: string): Promise<boolean> {
    try {
      const requestInfo: RequestInfo = {
        url: DELETE_SHOP_API(slug),
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `${accessToken.type} ${accessToken.sellerToken}`,
        },
      };
      const res: any = await asyncHandler(
        requestInfo,
        'DELETE_SHOP',
        'Shop deleted successfully',
        true,
        false,
        true
      );
      const success =
        res?.actionType === 'DELETE_SHOP' &&
        (res?.responseStatus === 204 || res?.data?.success === true);
      if (success) {
        dispatch(
          setMessage({
            message: 'Shop deleted successfully',
            messageType: 'success',
          })
        );
      }
      return !!success;
    } catch (e) {
      submitLogToBugsnag('error', `${e}`);
      return false;
    }
  }

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (userRole === 'agency') {
      if (!connectedShops.length) return;
    } else {
      if (!url || !isNotEmptyString(url)) return;
    }

    setLoading('pending');

    try {
      if (userRole === 'agency') {
        const primaryShop = connectedShops[0];
        const allShopsForProfile = connectedShops.map((shop) => ({
          id: shop.id ?? shop.slug,
          slug: shop.slug,
          name: shop.name,
          webshop_url: shop.url,
        }));

        dispatch(
          updateProfile({
            active_shop_slug: primaryShop.slug,
            all_shops: allShopsForProfile,
          })
        );
        dispatch(
          setUserCompanyDetails({
            shop_slug: primaryShop.slug,
            webshop_url: primaryShop.url,
            name: primaryShop.name,
          })
        );
      } else {
        const companyCreated = await createCompanyForSite(url);
        if (!companyCreated?.shop_slug) {
          throw new Error('Company creation failed');
        }
        const webshopDataSubmitted = await saveWebshopDataForSite(
          companyCreated,
          url
        );
        if (!webshopDataSubmitted) {
          throw new Error('Webshop data submission failed');
        }

        dispatch(
          updateProfile({
            active_shop_slug: companyCreated.shop_slug,
            all_shops: [
              {
                id: companyCreated.shop_id,
                slug: companyCreated.shop_slug,
                name: companyCreated.name,
                webshop_url: companyCreated.webshop_url,
              },
            ],
          })
        );
        dispatch(setUserCompanyDetails(companyCreated));
      }

      const sendOnboardingStatus = await update_all_shop_stats_api_call(
        3,
        false
      );
      if (!sendOnboardingStatus) {
        throw new Error('Onboarding status submission failed');
      }

      setLoading('initial');
      dispatch(
        setAiOnboardingStepCompleted({ stepNumber: 2, isCompleted: true })
      );
      dispatch(setAiOnboardingNextStep(3));
    } catch (error) {
      console.error('Error during submission:', error);
      setLoading('initial');
    }
  };

  const isDisabled =
    userRole === 'agency'
      ? connectedShops.length === 0
      : !url || !isNotEmptyString(url);

  const additionalSitesCount =
    userRole === 'agency' ? Math.max(0, connectedShops.length - 1) : 0;
  const shouldShowSitesCount = additionalSitesCount >= 1;

  return (
    <form onSubmit={submitForm} className='onboardingAddClient'>
      <OnboardingLogoPanel />
      <div className='onboardingAddClient__header'>
        <div className='onboardingAddClient__header-row'>
          <h1 className='onboardingAddClient__title'>
            {userRole === 'agency' ? 'Add your brands' : 'Add your website'}
          </h1>
        </div>
        <p className='onboardingAddClient__subtitle'>
          {userRole === 'agency'
            ? 'Add your client websites so TARA can start analyzing their performance.'
            : 'Enter your website URL so TARA can start analyzing your performance.'}
        </p>
      </div>

      <div className='onboardingAddClient__section'>
        <h2 className='onboardingAddClient__section-title'>
          {userRole === 'agency' ? 'Brand website address' : 'Website address'}{' '}
          *
        </h2>

        {userRole === 'agency' && (
          <p className='onboardingAddClient__description'>
            Add one or more brand websites. You can always add more later.
          </p>
        )}

        <div className='onboardingAddClient__input-wrapper'>
          <div className='onboardingAddClient__input-row'>
            <div
              className='onboardingAddClient__input-container'
              onClick={
                userRole === 'agency' ? () => setIsModalOpen(true) : undefined
              }
              style={userRole === 'agency' ? { cursor: 'pointer' } : undefined}
            >
              <AIURLInput
                prefix={url_prefix}
                label=''
                changeListeners={
                  userRole === 'agency'
                    ? [() => {}]
                    : [(e) => setURL(e.target.value)]
                }
                id='onboardingAddClient__shop-url-input'
                value={
                  userRole === 'agency'
                    ? connectedShops[0]?.url?.replace(/^https?:\/\//, '') ?? ''
                    : url
                }
                placeholder={
                  userRole === 'agency' ? 'brandsite.com' : 'yourwebsite.com'
                }
                title={t('URL-validation')}
                required={true}
                readOnly={userRole === 'agency'}
              />
            </div>
            {userRole === 'agency' && (
              <div className='onboardingAddClient__sites-actions'>
                {shouldShowSitesCount && (
                  <button
                    type='button'
                    className='onboardingAddClient__sites-count-button'
                    onClick={() => setIsModalOpen(true)}
                  >
                    +{additionalSitesCount} sites
                  </button>
                )}
                <button
                  type='button'
                  className='onboardingAddClient__sites-edit-button'
                  onClick={() => setIsModalOpen(true)}
                >
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M11.333 2.00001C11.5084 1.82445 11.7163 1.68507 11.9446 1.59128C12.1728 1.49749 12.4171 1.45117 12.6633 1.45523C12.9096 1.4593 13.1528 1.51368 13.3778 1.61478C13.6027 1.71589 13.805 1.86153 13.9717 2.04312C14.1383 2.22471 14.2658 2.43852 14.3469 2.67087C14.428 2.90321 14.461 3.14919 14.4437 3.39402C14.4264 3.63885 14.3591 3.87737 14.2459 4.09405C14.1328 4.31073 13.9762 4.50094 13.7863 4.65334L6.33301 12.1067L2.66634 13.3333L3.89301 9.66668L11.333 2.00001Z'
                      stroke='#667085'
                      strokeWidth='1.33333'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  {additionalSitesCount === 0 ? 'Add sites' : 'Edit sites'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='onboardingAddClient__footer'>
        <MainSquareButton
          type='submit'
          value='Continue'
          normal={true}
          loading={loading === 'pending'}
          disabled={isDisabled}
          style={{
            width: '100%',
            paddingTop: '1.2rem',
            paddingBottom: '1.2rem',
          }}
        />
      </div>

      <OnboardingFooter />

      {userRole === 'agency' && (
        <ConnectMultipleSitesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          shops={connectedShops}
          onShopsChange={setConnectedShops}
          onCreateShop={createShopForAgency}
          onUpdateShop={updateShopForAgency}
          onDeleteShop={deleteShopForAgency}
        />
      )}
    </form>
  );
};

export default OnboardingAddClient;
